import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, LogOut, User, HelpCircle, ChevronDown, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import ThemeToggle from './ThemeToggle';
import UserGuideModal from '../dashboard/components/UserGuideModal';

const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const role = user?.role || 'student';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileDropdownOpen && !event.target.closest('.profile-dropdown')) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileDropdownOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsProfileDropdownOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-200 
        ${isScrolled ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-md' : 'bg-white dark:bg-slate-900 shadow-sm'}`}>
        <div className="px-2 sm:px-3 mx-auto">
          <div className="flex items-center justify-between h-[70px]">
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 lg:hidden"
                aria-label="Toggle sidebar"
              >
                {isSidebarOpen ? <X size={22} /> : <Menu size={22} />}
              </button>

              <Link to="/" className="flex items-center">
                <img
                  src="/usiu-logo-02.png"
                  alt="USIU-Africa Logo"
                  className="h-9 w-auto transition-all duration-300"
                  onError={(e) => {
                    e.target.src = "/usiu-logo.png";
                  }}
                />
                <div className="ml-2 font-bold text-usiu-blue-600 dark:text-usiu-blue-400 transition-all duration-300">
                  <span className="hidden sm:inline">USIU-Africa</span>
                  <span className="sm:hidden">USIU</span>
                  <span className="block text-xs font-normal text-usiu-gold-500">Counseling Center</span>
                </div>
              </Link>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <ThemeToggle />

              <div className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-300">
                Welcome, {user?.username}
              </div>

              <div className="relative profile-dropdown">
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center gap-1.5 p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <User size={20} />
                  <ChevronDown size={16} className={`transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-slate-800 rounded-lg shadow-lg py-1 z-50 border border-gray-100 dark:border-gray-700">
                    <button
                      onClick={() => handleNavigation(`/${role}/profile`)}
                      className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                    >
                      <User size={16} className="mr-2" />
                      Profile
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <LogOut size={16} className="mr-2" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>

              {/* Help Icon */}
              <button
                onClick={() => setIsGuideModalOpen(true)}
                className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
              >
                <HelpCircle size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Security Alert Banner for Temporary Passwords */}
      {user?.isTempPassword && (
        <div className="fixed top-[70px] left-0 right-0 z-30 bg-amber-500 text-white py-2 px-4 shadow-md overflow-hidden">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 animate-pulse">
            <ShieldAlert size={20} className="shrink-0" />
            <p className="text-sm font-bold">
              SECURITY ALERT: You are using a temporary password.
              <button
                onClick={() => navigate(`/${role}/profile`)}
                className="ml-2 underline hover:text-amber-100 transition-colors"
              >
                Change it now to secure your account.
              </button>
            </p>
          </div>
        </div>
      )}

      <div className={`flex ${user?.isTempPassword ? 'pt-[106px]' : 'pt-[70px]'}`}>
        {/* Sidebar */}
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          role={role}
        />

        {/* Main content */}
        <main className={`flex-1 min-h-[calc(100vh-70px)] transition-all duration-300 ${isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'
          }`}>
          <div className="p-3 max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* User Guide Modal */}
      <UserGuideModal
        isOpen={isGuideModalOpen}
        onClose={() => setIsGuideModalOpen(false)}
        role={role}
      />
    </div>
  );
};

export default Layout;